import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Divider, Stack, Box, Typography, Paper } from '@mui/material'
import FormInput from './FormInput'
import localization from '../utils/localization'
import ExtraForm from './ExtraForm'
import ContentUtils from '../utils/content'

const BlockchainForm = forwardRef((props, ref) => {
  let walletDetail = props.contentInfo.detail
  if (!walletDetail) {
    walletDetail = {
      coinName: '',
      address: '',
      pubKey: '',
      priKey: '',
      keyStore: '',
      hintWord: '',
    }
  }
  const [title, setTitle] = useState(props.contentInfo.id ? props.contentInfo.title : '')
  const [titleErr, setTitleErr] = useState('')
  const [coinName, setCoinName] = useState(props.contentInfo.id ? walletDetail.coinName : '')
  const [address, setAddress] = useState(props.contentInfo.id ? walletDetail.address : '')
  const [pubKey, setPubKey] = useState(props.contentInfo.id ? walletDetail.pubKey : '')
  const [priKey, setPriKey] = useState(props.contentInfo.id ? walletDetail.priKey : '')
  const [keyStore, setKeyStore] = useState(props.contentInfo.id ? walletDetail.keyStore : '')
  const [mnemonicWord, setMnemonicWord] = useState(props.contentInfo.id ? walletDetail.hintWord : '')
  const [extra, setExtra] = useState(props.contentInfo.id ? props.contentInfo.extra : {})

  useEffect(() => {
    let walletDetail = props.contentInfo.detail
    if (!walletDetail) {
      walletDetail = {
        coinName: '',
        address: '',
        pubKey: '',
        priKey: '',
        keyStore: '',
        hintWord: '',
      }
    }
    if (props.contentInfo.id) {
      setTitle(props.contentInfo.title)
      setTitleErr('')
      setCoinName(walletDetail.coinName)
      setAddress(walletDetail.address)
      setPubKey(walletDetail.pubKey)
      setPriKey(walletDetail.priKey)
      setKeyStore(walletDetail.keyStore)
      setMnemonicWord(walletDetail.hintWord)
      setExtra(props.contentInfo.extra)
    }
  }, [props.contentInfo])

  useImperativeHandle(ref, () => ({
    checkForm() {
      let hasErr = false
      if (title === '') {
        setTitleErr(localization.strings.required)
        hasErr = true
      }
      return hasErr
    },
    getContentData() {
      const detail = {
        coinName: coinName,
        address: address,
        pubKey: pubKey,
        priKey: priKey,
        keyStore: keyStore,
        hintWord: mnemonicWord,
      }
      return {
        title: title,
        content: JSON.stringify(detail),
        detail: detail,
        type: ContentUtils.getBlockChainType(),
        color: 'blue',
        account: '',
        extra: extra,
      }
    },
  }))

  return (
    <Stack spacing={3}>
      {/* General Information */}
      <Stack spacing={2}>
        <FormInput
          label={localization.strings.title}
          value={title}
          placeholder={localization.strings.inputTitleHint}
          error={titleErr}
          onChange={(event) => {
            setTitle(event.target.value)
            setTitleErr('')
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <FormInput
              label={localization.strings.coinName}
              value={coinName}
              placeholder={localization.strings.inputCoinNameHint}
              onChange={(event) => setCoinName(event.target.value)}
            />
          </Box>
          <Box sx={{ flex: 2 }}>
            <FormInput
              label={localization.strings.address}
              value={address}
              placeholder={localization.strings.inputAddressHint}
              onChange={(event) => setAddress(event.target.value)}
            />
          </Box>
        </Stack>
      </Stack>

      <Divider sx={{ opacity: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 800, textTransform: 'uppercase' }}>
          {localization.strings.securityCredentials}
        </Typography>
      </Divider>

      {/* Security Credentials Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.04)',
          backgroundColor: 'rgba(255,255,255,0.5)',
        }}
      >
        <Stack spacing={2}>
          <FormInput
            type="password"
            label={localization.strings.pubkey}
            value={pubKey}
            placeholder={localization.strings.inputPubKeyHint}
            onChange={(event) => setPubKey(event.target.value)}
          />
          <FormInput
            type="password"
            label={localization.strings.privKey}
            value={priKey}
            placeholder={localization.strings.inputPriKeyHint}
            onChange={(event) => setPriKey(event.target.value)}
          />
          <FormInput
            type="password"
            label="KeyStore"
            value={keyStore}
            placeholder={localization.strings.inputKeyStoreHint}
            onChange={(event) => setKeyStore(event.target.value)}
          />
          <FormInput
            type="password"
            multiline={true}
            rows={2}
            label={localization.strings.mnemonic}
            value={mnemonicWord}
            placeholder={localization.strings.inputMnemonicHint}
            onChange={(event) => setMnemonicWord(event.target.value)}
          />
        </Stack>
      </Paper>

      <Divider sx={{ opacity: 0.5 }} />

      {/* Extra Metadata */}
      <ExtraForm extra={extra} onChange={(e) => setExtra(e)} />
    </Stack>
  )
})

export default BlockchainForm
